import React, { useState, useEffect } from 'react';
import { ConstructorEventUpdateSQLStatus } from "../../../../Event/event";

const QUESTIONS_PER_LEVEL = 25;

const SubtractFrom1000Game = () => {
  const [questions, setQuestions] = useState({ 1: [], 2: [] });
  const [current, setCurrent] = useState({ 1: 0, 2: 0 });
  const [level, setLevel] = useState(1);
  const [result, setResult] = useState({ 1: '', 2: '' });
  const [slots, setSlots] = useState({ 1: [], 2: [] });

  // Generate questions
  useEffect(() => {
    const generateQuestions = (min, max, count) => {
      const generated = [];
      for (let i = 0; i < count; i++) {
        let sub = Math.floor(Math.random() * (max - min + 1)) + min;
        let answer = (1000 - sub).toString().padStart(3, '0').split('');
        let layout = [
          '1', '0', '0', '0', '-', ...sub.toString().padStart(3, '0').split(''),
          '=', 'drop', 'drop', 'drop'
        ];
        let options = [...answer];
        while (options.length < 6) {
          let rand = Math.floor(Math.random() * 10).toString();
          if (!options.includes(rand)) options.push(rand);
        }
        generated.push({
          title: `1,000 - ${sub} = ?`,
          drops: answer,
          layout,
          options: options.sort(() => Math.random() - 0.5)
        });
      }
      return generated;
    };

    setQuestions({
      1: generateQuestions(0, 500, QUESTIONS_PER_LEVEL),
      2: generateQuestions(501, 999, QUESTIONS_PER_LEVEL),
    });
  }, []);

  // Reset slots on level or question change
  useEffect(() => {
    if (questions[level]?.length > 0) {
      const q = questions[level][current[level]];
      setSlots((prev) => ({
        ...prev,
        [level]: q.drops.map(() => '')
      }));
      setResult((prev) => ({ ...prev, [level]: '' }));
    }
  }, [current, level, questions]);

  const handleDrop = (index, value, lvl) => {
    const newSlots = [...slots[lvl]];
    newSlots[index] = value;
    setSlots((prev) => ({ ...prev, [lvl]: newSlots }));

    const q = questions[lvl][current[lvl]];
    const allFilled = newSlots.every(slot => slot !== '');

    if (allFilled) {
      const correct = q.drops.every((digit, idx) => newSlots[idx] === digit);
      setResult((prev) => ({
        ...prev,
        [lvl]: correct ? '🎉 Correct!' : '❌ Try again!'
      }));
    } else {
      setResult((prev) => ({ ...prev, [lvl]: '' }));
    }
  };

  const handleNext = (lvl) => {
    setCurrent((prev) => ({
      ...prev,
      [lvl]: (prev[lvl] + 1) % questions[lvl].length
    }));
    ConstructorEventUpdateSQLStatus();
  };

  const enableDrag = (e, value) => {
    e.dataTransfer.setData('text/plain', value);
  };

  const handleSlotDrop = (e, index, lvl) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('text/plain');
    handleDrop(index, data, lvl);
  };

  const gameStyles = {
    body: {
      fontFamily: "'Arial Rounded MT Bold', sans-serif",
      background: level === 1
        ? 'linear-gradient(to bottom, #ffecb3, #ffe082)'
        : 'linear-gradient(to bottom, #bbdefb, #90caf9)',
      margin: 0,
      padding: '1rem',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      flexDirection: 'column',
    },
    container: {
      background: '#ffffff',
      borderRadius: 20,
      padding: '2rem',
      width: '95%',
      maxWidth: '600px',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
      textAlign: 'center',
    },
    h1: {
      fontSize: '1.5rem',
      color: '#1565c0',
      wordWrap: 'break-word',
    },
    zone: {
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginTop: '1.2rem',
    },
    dropSlot: (filled, correct) => ({
      fontSize: '1.2rem',
      padding: '1rem',
      minWidth: '50px',
      minHeight: '50px',
      borderRadius: '10px',
      textAlign: 'center',
      cursor: 'pointer',
      backgroundColor: correct ? '#c8e6c9' : '#e3f2fd',
      border: correct ? '2px solid green' : '2px dashed #42a5f5',
      flex: '0 0 auto',
    }),
    dragItem: {
      fontSize: '1.2rem',
      padding: '0.8rem 1.2rem',
      borderRadius: '10px',
      textAlign: 'center',
      backgroundColor: '#2196f3',
      color: 'white',
      userSelect: 'none',
      cursor: 'grab',
      flex: '0 0 auto',
    },
    staticChar: {
      fontSize: '1.2rem',
      margin: '0 4px',
    },
    result: {
      fontSize: '1.1rem',
      marginTop: '1rem',
      color: '#0d47a1',
    },
    button: {
      marginTop: '1.5rem',
      padding: '0.8rem 1.5rem',
      fontSize: '1rem',
      borderRadius: '8px',
      border: 'none',
      backgroundColor: '#42a5f5',
      color: 'white',
      cursor: 'pointer',
    },
    levelSelector: {
      marginBottom: '1rem',
      display: 'flex',
      justifyContent: 'center',
      gap: '1rem',
    },
    toggleBtn: (active) => ({
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      border: 'none',
      cursor: 'pointer',
      backgroundColor: active ? '#1565c0' : '#90caf9',
      color: 'white',
      fontWeight: active ? 'bold' : 'normal',
    })
  };

  if (!questions[level] || questions[level].length === 0) {
    return <div style={gameStyles.body}>Loading...</div>;
  }

  const q = questions[level][current[level]];

  return (
    <div style={gameStyles.body}>
      <div style={gameStyles.container}>
        {/* 🔄 Level Toggle */}
        <div style={gameStyles.levelSelector}>
          <button
            style={gameStyles.toggleBtn(level === 1)}
            onClick={() => setLevel(1)}
          >
            Level 1 (0–500)
          </button>
          <button
            style={gameStyles.toggleBtn(level === 2)}
            onClick={() => setLevel(2)}
          >
            Level 2 (501–999)
          </button>
        </div>

        <h1 style={gameStyles.h1}>Level {level}: {q.title}</h1>

        <div style={gameStyles.zone}>
          {q.layout.map((item, i) => {
            if (item === 'drop') {
              return (
                <div
                  key={i}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleSlotDrop(e, i - q.layout.indexOf('drop'), level)}
                  style={gameStyles.dropSlot(slots[level][i - q.layout.indexOf('drop')] !== '', result[level] === '🎉 Correct!')}
                >
                  {slots[level][i - q.layout.indexOf('drop')] || ''}
                </div>
              );
            } else {
              return <span key={i} style={gameStyles.staticChar}>{item}</span>;
            }
          })}
        </div>

        <div style={gameStyles.zone}>
          {q.options.map((opt, idx) => (
            <div
              key={idx}
              style={gameStyles.dragItem}
              draggable
              onDragStart={(e) => enableDrag(e, opt)}
            >
              {opt}
            </div>
          ))}
        </div>

        <div style={gameStyles.result}>{result[level]}</div>

        <button style={gameStyles.button} onClick={() => handleNext(level)}>
          Next ➡️
        </button>
      </div>
    </div>
  );
};

export default SubtractFrom1000Game;
